import React, { FC, useCallback, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';

import { MessageTypeEnum } from './types';

interface JsonSchemaFormProps {
  schema: Record<string, any>;
  data: Record<string, any>;
  onSubmit: (formData: Record<string, any>) => void;
  isReadOnly: boolean;
}

const JSON_SCHEMA_APP_URL = process.env.NX_JSON_SCHEMA_APP_URL;

const JsonSchemaForm: FC<JsonSchemaFormProps> = ({
  schema,
  data = {},
  onSubmit,
  isReadOnly,
}) => {
  const iframeRef = useRef(null);

  const sendInitialFormConfig = useCallback(() => {
    if (!iframeRef.current) return;

    iframeRef.current.contentWindow.postMessage(
      {
        type: MessageTypeEnum.InitFormConfig,
        schema,
        formData: data,
        isReadOnly,
      },
      JSON_SCHEMA_APP_URL
    );
  }, [data, isReadOnly, schema]);

  const onMessage = useCallback(
    (event) => {
      if (event.origin !== JSON_SCHEMA_APP_URL) return;

      const { type } = event.data;

      switch (type) {
        case MessageTypeEnum.SubmitFormData: {
          const { formData } = event.data;

          onSubmit(formData);
          break;
        }

        case MessageTypeEnum.OnLoad: {
          sendInitialFormConfig();
        }
      }
    },
    [onSubmit, sendInitialFormConfig]
  );

  useEffectOnce(function subscribeToIframeMessages() {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  });

  return (
    <Container>
      <Iframe ref={iframeRef} src={JSON_SCHEMA_APP_URL} />
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  overflow: hidden;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

export default JsonSchemaForm;
